<template>
  <div class="field-editor">
    <!-- Header -->
    <div class="editor-header">
      <div class="editor-title">
        <i :class="getFieldIcon(getFieldTypeName(localField))"></i>
        <span>{{ getFieldTypeLabel(localField) }}</span>
      </div>
      <button class="btn-close-editor" @click="$emit('close')">
        <i class="bx bx-x"></i>
      </button>
    </div>

    <!-- Tabs -->
    <div class="editor-tabs">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'general' }"
        @click="activeTab = 'general'"
      >
        Geral
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'validation' }"
        @click="activeTab = 'validation'"
      >
        Validação
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'conditions' }"
        @click="activeTab = 'conditions'"
      >
        Condições
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'layout' }"
        @click="activeTab = 'layout'"
      >
        Layout
      </button>
    </div>

    <!-- Content -->
    <div class="editor-content">
      <!-- General Tab -->
      <div v-show="activeTab === 'general'" class="tab-content">
        <!-- Language Tabs (when multi-language is enabled) -->
        <div v-if="isMultiLanguage && activeLanguages.length > 1" class="language-tabs-container">
          <div class="language-tabs">
            <button
              v-for="lang in activeLanguages"
              :key="lang.id"
              class="language-tab"
              :class="getLanguageTabClass(lang)"
              @click="selectLanguage(lang.id)"
            >
              <span v-if="lang.flag" class="lang-flag">{{ lang.flag }}</span>
              <span class="lang-name">{{ lang.name }}</span>
              <span v-if="lang.is_default" class="badge-default">Padrão</span>
            </button>
          </div>
          <small class="language-hint">
            <i class="bx bx-globe"></i>
            Editando em: <strong>{{ currentLanguage ? currentLanguage.native_name : '' }}</strong>
          </small>
        </div>

        <div class="form-group">
          <label>Label *</label>
          <input
            :value="getTranslationValue('label')"
            type="text"
            class="form-control form-control-sm"
            :placeholder="isDefaultLanguage ? '' : `Tradução do label para ${currentLanguage ? currentLanguage.native_name : ''}`"
            @input="setTranslationValue('label', $event.target.value)"
          />
        </div>

        <div class="form-group" v-if="isDefaultLanguage">
          <label>Nome do campo (slug)</label>
          <input
            v-model="localField.name"
            type="text"
            class="form-control form-control-sm"
            :disabled="isLockedNameField"
            @input="emitUpdate"
          />
          <small v-if="isLockedNameField" class="text-warning">
            <i class="bx bx-lock-alt"></i>
            Campo padrão do sistema - nome não pode ser alterado
          </small>
          <small v-else class="text-muted"
            >Usado internamente para identificar o campo</small
          >
        </div>

        <div class="form-group">
          <label>Placeholder</label>
          <input
            :value="getTranslationValue('placeholder')"
            type="text"
            class="form-control form-control-sm"
            :placeholder="isDefaultLanguage ? '' : `Tradução do placeholder para ${currentLanguage ? currentLanguage.native_name : ''}`"
            @input="setTranslationValue('placeholder', $event.target.value)"
          />
        </div>

        <div class="form-group">
          <label>Texto de ajuda</label>
          <input
            :value="getTranslationValue('help_text')"
            type="text"
            class="form-control form-control-sm"
            :placeholder="isDefaultLanguage ? '' : `Tradução do texto de ajuda para ${currentLanguage ? currentLanguage.native_name : ''}`"
            @input="setTranslationValue('help_text', $event.target.value)"
          />
        </div>

        <div class="form-group" v-if="isDefaultLanguage">
          <label>Valor padrão</label>
          <input
            v-model="localField.default_value"
            type="text"
            class="form-control form-control-sm"
            @input="emitUpdate"
          />
        </div>

        <!-- Options for select, radio, checkbox, multiselect -->
        <div v-if="hasOptions" class="form-group">
          <label>
            Opções
            <span v-if="!isDefaultLanguage" class="text-muted ml-1">(apenas labels traduzíveis)</span>
          </label>
          <div class="options-editor">
            <div
              v-for="(opt, index) in localField.options"
              :key="index"
              class="option-row"
            >
              <input
                :value="getOptionTranslationValue(index)"
                type="text"
                class="form-control form-control-sm"
                :placeholder="isDefaultLanguage ? 'Label' : `Tradução: ${opt.label}`"
                @input="setOptionTranslationValue(index, $event.target.value)"
              />
              <span v-if="!isDefaultLanguage" class="option-value-hint">{{ opt.value }}</span>
              <button
                v-if="isDefaultLanguage && localField.options.length > 1"
                class="btn-remove-opt"
                @click="removeOption(index)"
              >
                <i class="bx bx-x"></i>
              </button>
            </div>
            <button v-if="isDefaultLanguage" class="btn-add-opt" @click="addOption">
              <i class="bx bx-plus"></i> Adicionar opção
            </button>
          </div>
        </div>

        <!-- Autocomplete Config (for city and autocomplete types) -->
        <div v-if="isAutocompleteType && isDefaultLanguage" class="form-group">
          <label>Fonte de dados (Autocomplete)</label>
          <select
            v-model="autocompleteSource"
            class="form-control form-control-sm"
            @change="onAutocompleteSourceChange"
          >
            <option value="">Selecione uma lista</option>
            <option value="cities">Cidades do Brasil (padrão)</option>
            <option
              v-for="cl in customLists"
              :key="cl.id"
              :value="cl.slug"
            >
              {{ cl.name }}
            </option>
          </select>
        </div>

        <div v-if="isAutocompleteType && isDefaultLanguage && autocompleteDeps.length > 0" class="form-group">
          <label>Dependências <small class="text-muted">(filtrar resultados por campos do formulário)</small></label>
          <div v-for="(dep, index) in autocompleteDeps" :key="index" class="d-flex align-items-center mb-2">
            <span class="mr-2 text-muted" style="min-width: 80px">{{ dep.label }}:</span>
            <select
              v-model="dep.fieldName"
              class="form-control form-control-sm"
              @change="onDependencyChange"
            >
              <option value="">Nenhum</option>
              <option
                v-for="f in availableSourceFields"
                :key="f.id || f._tempId"
                :value="f.name"
              >
                {{ f.label || f.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Validation Tab -->
      <div v-show="activeTab === 'validation'" class="tab-content">
        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="localField.required"
              :disabled="isProtectedField"
              @change="emitUpdate"
            />
            <span>Campo obrigatório</span>
            <small v-if="isProtectedField" class="text-warning ml-2">
              (sempre obrigatório)
            </small>
          </label>
        </div>

        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="localField.unique_value"
              :disabled="isEmailField"
              @change="emitUpdate"
            />
            <span>Valor único (não permite duplicados)</span>
            <small v-if="isEmailField" class="text-warning ml-2">
              (sempre único)
            </small>
          </label>
        </div>

        <template v-if="isTextType">
          <div class="form-group">
            <label>Tamanho mínimo</label>
            <input
              v-model.number="localField.min_length"
              type="number"
              class="form-control form-control-sm"
              min="0"
              @input="emitUpdate"
            />
          </div>

          <div class="form-group">
            <label>Tamanho máximo</label>
            <input
              v-model.number="localField.max_length"
              type="number"
              class="form-control form-control-sm"
              min="0"
              @input="emitUpdate"
            />
          </div>
        </template>

        <template v-if="getFieldTypeName(localField) === 'number'">
          <div class="form-group">
            <label>Valor mínimo</label>
            <input
              v-model.number="localField.min_value"
              type="number"
              class="form-control form-control-sm"
              @input="emitUpdate"
            />
          </div>

          <div class="form-group">
            <label>Valor máximo</label>
            <input
              v-model.number="localField.max_value"
              type="number"
              class="form-control form-control-sm"
              @input="emitUpdate"
            />
          </div>
        </template>

        <template
          v-if="
            getFieldTypeName(localField) === 'file' ||
            getFieldTypeName(localField) === 'image'
          "
        >
          <div class="form-group">
            <label>Extensões permitidas</label>
            <input
              v-model="localField.allowed_extensions"
              type="text"
              class="form-control form-control-sm"
              placeholder="jpg,png,pdf"
              @input="emitUpdate"
            />
            <small class="text-muted">Separadas por vírgula</small>
          </div>

          <div class="form-group">
            <label>Tamanho máximo (MB)</label>
            <input
              v-model.number="localField.max_file_size"
              type="number"
              class="form-control form-control-sm"
              min="0"
              @input="emitUpdate"
            />
          </div>
        </template>
      </div>

      <!-- Conditions Tab -->
      <div v-show="activeTab === 'conditions'" class="tab-content">
        <div class="conditions-intro">
          <p>Configure quando este campo deve ser exibido ou ocultado.</p>
        </div>

        <div
          v-for="(condition, condIndex) in localField.conditions"
          :key="condIndex"
          class="condition-card"
        >
          <div class="condition-header">
            <span>Condição {{ condIndex + 1 }}</span>
            <button
              class="btn-remove-condition"
              @click="removeCondition(condIndex)"
            >
              <i class="bx bx-trash"></i>
            </button>
          </div>

          <div class="form-group">
            <label>Ação</label>
            <select
              v-model="condition.action"
              class="form-control form-control-sm"
              @change="emitUpdate"
            >
              <option value="show">Mostrar campo</option>
              <option value="hide">Ocultar campo</option>
              <option value="enable">Habilitar campo</option>
              <option value="disable">Desabilitar campo</option>
              <option value="require">Tornar obrigatório</option>
            </select>
          </div>

          <div class="form-group">
            <label>Lógica</label>
            <select
              v-model="condition.logic"
              class="form-control form-control-sm"
              @change="emitUpdate"
            >
              <option value="and">Todas as regras (E)</option>
              <option value="or">Qualquer regra (OU)</option>
            </select>
          </div>

          <!-- Rules -->
          <div class="condition-rules">
            <div
              v-for="(rule, ruleIndex) in condition.rules"
              :key="ruleIndex"
              class="rule-row"
            >
              <select
                v-model="rule.source_field_id"
                class="form-control form-control-sm"
                @change="emitUpdate"
              >
                <option :value="null">Selecione o campo</option>
                <option
                  v-for="f in availableSourceFields"
                  :key="f.id || f._tempId"
                  :value="f.id || f._tempId"
                >
                  {{ f.label }}
                </option>
              </select>

              <select
                v-model="rule.operator"
                class="form-control form-control-sm"
                @change="emitUpdate"
              >
                <option value="==">Igual a</option>
                <option value="!=">Diferente de</option>
                <option value="contains">Contém</option>
                <option value="not_contains">Não contém</option>
                <option value="empty">Está vazio</option>
                <option value="not_empty">Não está vazio</option>
                <option value=">">Maior que</option>
                <option value="<">Menor que</option>
                <option value=">=">Maior ou igual</option>
                <option value="<=">Menor ou igual</option>
              </select>

              <input
                v-if="!['empty', 'not_empty'].includes(rule.operator)"
                v-model="rule.value"
                type="text"
                class="form-control form-control-sm"
                placeholder="Valor"
                @input="emitUpdate"
              />

              <button
                class="btn-remove-rule"
                @click="removeRule(condIndex, ruleIndex)"
                v-if="condition.rules.length > 1"
              >
                <i class="bx bx-x"></i>
              </button>
            </div>

            <button class="btn-add-rule" @click="addRule(condIndex)">
              <i class="bx bx-plus"></i> Adicionar regra
            </button>
          </div>
        </div>

        <button class="btn-add-condition" @click="addCondition">
          <i class="bx bx-plus"></i> Adicionar condição
        </button>
      </div>

      <!-- Layout Tab -->
      <div v-show="activeTab === 'layout'" class="tab-content">
        <div class="form-group">
          <label>Largura do campo</label>
          <div class="layout-options">
            <button
              class="layout-btn"
              :class="{ active: getLayoutCols() === 12 }"
              @click="setLayoutCols(12)"
            >
              <div class="layout-preview full"></div>
              <span>100%</span>
            </button>
            <button
              class="layout-btn"
              :class="{ active: getLayoutCols() === 6 }"
              @click="setLayoutCols(6)"
            >
              <div class="layout-preview half"></div>
              <span>50%</span>
            </button>
            <button
              class="layout-btn"
              :class="{ active: getLayoutCols() === 4 }"
              @click="setLayoutCols(4)"
            >
              <div class="layout-preview third"></div>
              <span>33%</span>
            </button>
            <button
              class="layout-btn"
              :class="{ active: getLayoutCols() === 3 }"
              @click="setLayoutCols(3)"
            >
              <div class="layout-preview quarter"></div>
              <span>25%</span>
            </button>
          </div>
        </div>

        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="localField.hidden"
              @change="emitUpdate"
            />
            <span>Campo oculto no formulário</span>
          </label>
        </div>

        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="localField.readonly"
              @change="emitUpdate"
            />
            <span>Campo somente leitura</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCustomLists } from '../services'

export default {
  name: 'FieldEditor',
  props: {
    field: {
      type: Object,
      required: true
    },
    allFields: {
      type: Array,
      default: () => []
    },
    activeLanguages: {
      type: Array,
      default: () => []
    },
    isMultiLanguage: {
      type: Boolean,
      default: false
    },
    defaultLanguageId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      activeTab: 'general',
      currentLanguageId: null,
      localField: {},
      customLists: [],
      autocompleteSource: '',
      autocompleteDeps: [],
      fieldIcons: {
        text: 'bx bx-text',
        textarea: 'bx bx-align-left',
        email: 'bx bx-envelope',
        phone: 'bx bx-phone',
        number: 'bx bx-hash',
        password: 'bx bx-lock',
        hidden: 'bx bx-hide',
        url: 'bx bx-link',
        select: 'bx bx-chevron-down',
        multiselect: 'bx bx-list-check',
        checkbox: 'bx bx-checkbox-checked',
        radio: 'bx bx-radio-circle-marked',
        toggle: 'bx bx-toggle-right',
        cpf: 'bx bx-id-card',
        cnpj: 'bx bx-building',
        cpf_cnpj: 'bx bx-id-card',
        cep: 'bx bx-map-pin',
        state: 'bx bx-map',
        city: 'bx bx-buildings',
        rg: 'bx bx-id-card',
        date: 'bx bx-calendar',
        time: 'bx bx-time',
        datetime: 'bx bx-calendar-event',
        birthdate: 'bx bx-cake',
        file: 'bx bx-upload',
        image: 'bx bx-image',
        rating: 'bx bx-star',
        terms: 'bx bx-check-shield',
        heading: 'bx bx-heading',
        paragraph: 'bx bx-paragraph',
        divider: 'bx bx-minus'
      }
    }
  },
  computed: {
    hasOptions () {
      const optionTypes = ['select', 'multiselect', 'radio', 'checkbox']
      return optionTypes.includes(this.getFieldTypeName(this.localField))
    },

    isAutocompleteType () {
      const types = ['city', 'autocomplete']
      return types.includes(this.getFieldTypeName(this.localField))
    },

    isTextType () {
      const textTypes = [
        'text',
        'textarea',
        'email',
        'phone',
        'password',
        'url'
      ]
      return textTypes.includes(this.getFieldTypeName(this.localField))
    },

    isLockedNameField () {
      const typeName = this.getFieldTypeName(this.localField)
      const isStandard = this.localField.fieldType?.is_standard ||
        this.localField.field_type?.is_standard ||
        false
      // Lock name for standard fields (name, email, phone)
      return ['name', 'email', 'phone'].includes(typeName) || isStandard
    },

    isProtectedField () {
      const typeName = this.getFieldTypeName(this.localField)
      // Protected fields cannot be deleted (name, email)
      return ['name', 'email'].includes(typeName)
    },

    isEmailField () {
      return this.getFieldTypeName(this.localField) === 'email'
    },

    availableSourceFields () {
      const currentId = this.localField.id || this.localField._tempId
      return this.allFields.filter(f => {
        const fId = f.id || f._tempId
        return fId !== currentId
      })
    },

    // Translation computed
    translatableFields () {
      // Fields that can be translated
      return ['label', 'placeholder', 'help_text']
    },

    currentLanguage () {
      if (!this.currentLanguageId) return null
      return this.activeLanguages.find(l => l.id === this.currentLanguageId)
    },

    isDefaultLanguage () {
      return this.currentLanguageId === this.defaultLanguageId
    }
  },
  watch: {
    field: {
      immediate: true,
      deep: true,
      handler (val) {
        this.localField = JSON.parse(JSON.stringify(val))
        if (!this.localField.conditions) {
          this.localField.conditions = []
        }
        if (!this.localField.layout) {
          this.localField.layout = { cols: 12 }
        }
        if (!this.localField.options && this.hasOptions) {
          this.localField.options = [{ label: '', value: '' }]
        }
        // Initialize translations object if not present
        if (!this.localField.translations) {
          this.localField.translations = {}
        }
        // Initialize config object if not present
        if (!this.localField.config) {
          this.localField.config = {}
        }
        // Initialize autocomplete config from field config
        this.initAutocompleteConfig()
      }
    },
    defaultLanguageId: {
      immediate: true,
      handler (val) {
        if (val && !this.currentLanguageId) {
          this.currentLanguageId = val
        }
      }
    }
  },
  methods: {
    getFieldTypeName (field) {
      return (
        field.fieldType?.name ||
        field.field_type?.name ||
        field.fieldTypeName ||
        'text'
      )
    },

    getFieldTypeLabel (field) {
      return (
        field.fieldType?.label ||
        field.field_type?.label ||
        this.getFieldTypeName(field)
      )
    },

    getFieldIcon (typeName) {
      return this.fieldIcons[typeName] || 'bx bx-text'
    },

    getLayoutCols () {
      return this.localField.layout?.cols || 12
    },

    setLayoutCols (cols) {
      if (!this.localField.layout) {
        this.localField.layout = {}
      }
      this.localField.layout.cols = cols
      this.emitUpdate()
    },

    emitUpdate () {
      this.$emit('update-field', { ...this.localField })
    },

    // Options
    addOption () {
      if (!this.localField.options) {
        this.localField.options = []
      }
      this.localField.options.push({ label: '', value: '' })
      this.emitUpdate()
    },

    removeOption (index) {
      this.localField.options.splice(index, 1)
      this.emitUpdate()
    },

    updateOptionAndEmit (index) {
      const opt = this.localField.options[index]
      if (opt.label) {
        opt.value = opt.label
      }
      this.emitUpdate()
    },

    slugify (text) {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
    },

    // Conditions
    addCondition () {
      if (!this.localField.conditions) {
        this.localField.conditions = []
      }
      this.localField.conditions.push({
        action: 'show',
        logic: 'and',
        rules: [
          {
            source_field_id: null,
            operator: '==',
            value: ''
          }
        ]
      })
      this.emitUpdate()
    },

    removeCondition (index) {
      this.localField.conditions.splice(index, 1)
      this.emitUpdate()
    },

    addRule (condIndex) {
      this.localField.conditions[condIndex].rules.push({
        source_field_id: null,
        operator: '==',
        value: ''
      })
      this.emitUpdate()
    },

    removeRule (condIndex, ruleIndex) {
      this.localField.conditions[condIndex].rules.splice(ruleIndex, 1)
      this.emitUpdate()
    },

    // Translation methods
    selectLanguage (langId) {
      this.currentLanguageId = langId
    },

    getTranslationValue (field) {
      // For default language, use the main field value
      if (this.isDefaultLanguage) {
        return this.localField[field] || ''
      }
      // For other languages, use translations object
      if (this.localField.translations &&
          this.localField.translations[this.currentLanguageId] &&
          this.localField.translations[this.currentLanguageId][field] !== undefined) {
        return this.localField.translations[this.currentLanguageId][field]
      }
      return ''
    },

    setTranslationValue (field, value) {
      // For default language, update the main field
      if (this.isDefaultLanguage) {
        this.localField[field] = value
      } else {
        // For other languages, update translations object
        if (!this.localField.translations) {
          this.localField.translations = {}
        }
        if (!this.localField.translations[this.currentLanguageId]) {
          this.localField.translations[this.currentLanguageId] = {}
        }
        this.localField.translations[this.currentLanguageId][field] = value
      }
      this.emitUpdate()
    },

    getOptionTranslationValue (index) {
      // For default language, return main option label
      if (this.isDefaultLanguage) {
        return this.localField.options[index]?.label || ''
      }
      // For other languages, get from translations
      if (this.localField.translations &&
          this.localField.translations[this.currentLanguageId] &&
          this.localField.translations[this.currentLanguageId].options &&
          this.localField.translations[this.currentLanguageId].options[index]) {
        return this.localField.translations[this.currentLanguageId].options[index].label || ''
      }
      return ''
    },

    setOptionTranslationValue (index, value) {
      if (this.isDefaultLanguage) {
        // Update main option
        if (this.localField.options[index]) {
          this.localField.options[index].label = value
          // Auto-generate value from label
          this.localField.options[index].value = value
        }
      } else {
        // Update translation
        if (!this.localField.translations) {
          this.localField.translations = {}
        }
        if (!this.localField.translations[this.currentLanguageId]) {
          this.localField.translations[this.currentLanguageId] = {}
        }
        if (!this.localField.translations[this.currentLanguageId].options) {
          this.localField.translations[this.currentLanguageId].options = []
        }
        // Ensure array has enough slots
        while (this.localField.translations[this.currentLanguageId].options.length <= index) {
          this.localField.translations[this.currentLanguageId].options.push({ label: '' })
        }
        this.localField.translations[this.currentLanguageId].options[index] = { label: value }
      }
      this.emitUpdate()
    },

    getLanguageTabClass (lang) {
      return {
        active: this.currentLanguageId === lang.id,
        'is-default': lang.is_default
      }
    },

    // Autocomplete methods
    async loadCustomLists () {
      try {
        const res = await getCustomLists()
        this.customLists = res.data.data || []
      } catch (e) {
        this.customLists = []
      }
    },

    initAutocompleteConfig () {
      if (!this.isAutocompleteType) return

      // Load custom lists if not loaded
      if (!this.customLists.length) {
        this.loadCustomLists()
      }

      const config = this.localField.config || {}

      // Determine source
      if (config.autocomplete_url && config.autocomplete_url.includes('/custom-lists/')) {
        // Extract slug from URL: /utils/custom-lists/{slug}/items
        const match = config.autocomplete_url.match(/\/custom-lists\/([^/]+)\/items/)
        this.autocompleteSource = match ? match[1] : ''
      } else if (config.autocomplete_url === '/utils/cities' || this.getFieldTypeName(this.localField) === 'city') {
        this.autocompleteSource = 'cities'
      } else {
        this.autocompleteSource = ''
      }

      // Build deps from depends_on
      this.buildDepsFromConfig(config)
    },

    buildDepsFromConfig (config) {
      const dependsOn = config.depends_on

      if (!dependsOn) {
        this.autocompleteDeps = []
        return
      }

      if (typeof dependsOn === 'string') {
        this.autocompleteDeps = [{ key: 'uf', label: 'Estado (UF)', fieldName: dependsOn }]
      } else if (typeof dependsOn === 'object') {
        this.autocompleteDeps = Object.entries(dependsOn).map(([key, fieldName]) => ({
          key,
          label: this.getDepLabel(key),
          fieldName
        }))
      }
    },

    getDepLabel (key) {
      const labels = { uf: 'Estado (UF)', cidade: 'Cidade', estado: 'Estado' }
      return labels[key] || key
    },

    onAutocompleteSourceChange () {
      if (!this.localField.config) {
        this.localField.config = {}
      }

      if (this.autocompleteSource === 'cities') {
        this.localField.config.autocomplete_url = '/utils/cities'
        this.autocompleteDeps = [{ key: 'uf', label: 'Estado (UF)', fieldName: 'estado' }]
      } else if (this.autocompleteSource) {
        // Custom list selected
        this.localField.config.autocomplete_url = '/utils/custom-lists/' + this.autocompleteSource + '/items'
        // Build deps from list columns that are filterable
        const list = this.customLists.find(l => l.slug === this.autocompleteSource)
        if (list && list.columns) {
          this.autocompleteDeps = list.columns
            .filter(col => col.filterable)
            .map(col => {
              // Auto-match: try to find a form field with the same name or similar
              const autoMatch = this.availableSourceFields.find(f =>
                f.name === col.key ||
                f.name === col.label.toLowerCase() ||
                (col.key === 'uf' && this.getFieldTypeName(f) === 'state') ||
                (col.key === 'cidade' && this.getFieldTypeName(f) === 'city')
              )
              return { key: col.key, label: col.label, fieldName: autoMatch ? autoMatch.name : '' }
            })
        } else {
          this.autocompleteDeps = []
        }
      } else {
        this.localField.config.autocomplete_url = null
        this.autocompleteDeps = []
      }

      this.updateConfigFromDeps()
      this.emitUpdate()
    },

    onDependencyChange () {
      this.updateConfigFromDeps()
      this.emitUpdate()
    },

    updateConfigFromDeps () {
      if (!this.localField.config) {
        this.localField.config = {}
      }

      if (this.autocompleteDeps.length === 0) {
        this.localField.config.depends_on = null
      } else if (this.autocompleteDeps.length === 1) {
        this.localField.config.depends_on = this.autocompleteDeps[0].fieldName || null
      } else {
        const depsObj = {}
        this.autocompleteDeps.forEach(dep => {
          if (dep.fieldName) {
            depsObj[dep.key] = dep.fieldName
          }
        })
        this.localField.config.depends_on = Object.keys(depsObj).length ? depsObj : null
      }
    }
  }
}
</script>

<style scoped>
.field-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

/* Header */
.editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
}

.editor-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.editor-title i {
  font-size: 18px;
  color: #556ee6;
}

.btn-close-editor {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #6c757d;
  cursor: pointer;
}

.btn-close-editor:hover {
  background: #f3f4f6;
  color: #344054;
}

/* Tabs */
.editor-tabs {
  display: flex;
  border-bottom: 1px solid #e9ecef;
  padding: 0 8px;
}

.tab-btn {
  flex: 1;
  padding: 10px 8px;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.15s;
}

.tab-btn:hover {
  color: #495057;
}

.tab-btn.active {
  color: #556ee6;
  border-bottom-color: #556ee6;
}

/* Content */
.editor-content {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.tab-content {
  padding: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 6px;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-label span {
  font-size: 13px;
  color: #495057;
}

/* Options Editor */
.options-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.option-row .form-control-sm {
  flex: 1;
}

.btn-remove-opt {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-add-opt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  border-radius: 6px;
  font-size: 12px;
  color: #6c757d;
  cursor: pointer;
}

.btn-add-opt:hover {
  border-color: #556ee6;
  color: #556ee6;
}

/* Conditions */
.conditions-intro {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.conditions-intro p {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
}

.condition-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.condition-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.btn-remove-condition {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
  cursor: pointer;
}

.condition-rules {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
}

.rule-row {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.rule-row select,
.rule-row input {
  flex: 1;
  min-width: 80px;
}

.btn-remove-rule {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-add-rule {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  border-radius: 4px;
  font-size: 11px;
  color: #6c757d;
  cursor: pointer;
}

.btn-add-rule:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.btn-add-condition {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  border: 1px dashed #556ee6;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  color: #556ee6;
  cursor: pointer;
}

.btn-add-condition:hover {
  background: #f0f4ff;
}

/* Layout Options */
.layout-options {
  display: flex;
  gap: 8px;
}

.layout-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.layout-btn:hover {
  border-color: #556ee6;
}

.layout-btn.active {
  border-color: #556ee6;
  background: #f0f4ff;
}

.layout-btn span {
  font-size: 11px;
  color: #6c757d;
}

.layout-preview {
  height: 8px;
  background: #556ee6;
  border-radius: 2px;
}

.layout-preview.full {
  width: 100%;
}

.layout-preview.half {
  width: 50%;
}

.layout-preview.third {
  width: 33%;
}

.layout-preview.quarter {
  width: 25%;
}

/* Language Tabs */
.language-tabs-container {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.language-tabs {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.language-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 11px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}

.language-tab:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.language-tab.active {
  border-color: #556ee6;
  background: #f0f4ff;
  color: #556ee6;
}

.language-tab .lang-flag {
  font-size: 14px;
}

.language-tab .badge-default {
  padding: 2px 4px;
  background: #d1fae5;
  color: #059669;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 500;
}

.language-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #9ca3af;
}

.language-hint i {
  font-size: 12px;
}

.language-hint strong {
  color: #556ee6;
}

/* Option value hint for non-default languages */
.option-value-hint {
  flex: 0 0 80px;
  font-size: 11px;
  color: #9ca3af;
  padding: 6px 8px;
  background: #f8f9fa;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
